import { useEffect, useRef, useMemo } from "react"

import { useAppDispatch } from "../../../hooks/use-app-dispatch.ts"
import { useAppSelector } from "../../../hooks/use-app-selector.ts"
import {
  SEARCH_DEBOUNCE_MS,
  recordLayers
} from "../redux/global-map.constants"
import {
  selectActiveFilters,
  selectActiveLayers,
  selectAnalyticsState,
  selectSearchTerm
} from "../redux/global-map.slice"
import { fetchAnalytics } from "../redux/global-map.thunks"
import type {
  AnalyticsResponse,
  EntityType,
  GlobalMapFilters
} from "../redux/global-map.types"

interface UseAnalyticsResult {
  data: AnalyticsResponse | null
  status: "idle" | "loading" | "refreshing" | "error"
  error: { code: string; retryable: boolean } | null
  refetch: () => void
}

/**
 * Owns what keeps the Analytics tab's data in sync — mirrors
 * `useListFeatures.ts` deliberately (mount fetch, filters/search/layers
 * triggers, each guarded by a value-comparison ref rather than a "skip the
 * first run" boolean), minus everything List-only (pagination, sort,
 * sub-tabs): Analytics has none of those. `AnalyticsTab` only mounts while
 * `activeTab === "analytics"` (`MapTab.tsx` renders it conditionally, the
 * same way it renders `ListTab`), so this hook's own mount effect IS the
 * "only fetch while this tab is open" gate — no separate `ready`/tab check
 * needed inside the hook itself.
 */
export function useAnalytics(): UseAnalyticsResult {
  const dispatch = useAppDispatch()
  const filters = useAppSelector(selectActiveFilters)
  const searchTerm = useAppSelector(selectSearchTerm)
  const layers = useAppSelector(selectActiveLayers)
  // Only the record layers reach this endpoint — the two marker layers
  // (`subcontractor`/`appointment`) are drawn on the map and scope nothing
  // here. Memoized on the store's own array identity, which is what the
  // reference comparisons in the effects below treat as "the layers changed".
  const activeLayers = useMemo(() => recordLayers(layers), [layers])
  const { data, status, error } = useAppSelector(selectAnalyticsState)

  const hasFetchedInitial = useRef(false)
  const lastFetchedFiltersRef = useRef<GlobalMapFilters | null>(null)
  const lastFetchedLayersRef = useRef<EntityType[] | null>(null)
  const lastFetchedSearchRef = useRef(searchTerm)

  useEffect(() => {
    hasFetchedInitial.current = true
    lastFetchedFiltersRef.current = filters
    lastFetchedLayersRef.current = activeLayers

    dispatch(
      fetchAnalytics({ filters, search: searchTerm, entities: activeLayers })
    )
    // Mount-only — `[dispatch]` alone is the deliberate trigger (mirrors
    // `useListFeatures.ts`'s own mount effect keyed on `[ready, dispatch]`;
    // this hook has no `ready` gate). `filters`/`searchTerm`/`activeLayers`
    // are read once here and handled by their own dedicated effects below.
  }, [dispatch])

  useEffect(() => {
    if (!hasFetchedInitial.current) return
    if (filters === lastFetchedFiltersRef.current) return
    lastFetchedFiltersRef.current = filters

    dispatch(
      fetchAnalytics({ filters, search: searchTerm, entities: activeLayers })
    )
  }, [filters, dispatch])

  useEffect(() => {
    if (searchTerm === lastFetchedSearchRef.current) return
    lastFetchedSearchRef.current = searchTerm

    const handle = setTimeout(() => {
      dispatch(
        fetchAnalytics({ filters, search: searchTerm, entities: activeLayers })
      )
    }, SEARCH_DEBOUNCE_MS)

    return () => clearTimeout(handle)
  }, [searchTerm, dispatch])

  useEffect(() => {
    if (!hasFetchedInitial.current) return
    if (activeLayers === lastFetchedLayersRef.current) return
    lastFetchedLayersRef.current = activeLayers

    dispatch(
      fetchAnalytics({ filters, search: searchTerm, entities: activeLayers })
    )
  }, [activeLayers, dispatch])

  const refetch = () => {
    dispatch(
      fetchAnalytics({ filters, search: searchTerm, entities: activeLayers })
    )
  }

  return { data, status, error, refetch }
}
